import { useCallback } from 'react';
import { useApplicationContext } from '../useApplicationContext';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

export enum TimezoneFormatType {
  NAME = 'name`',
  OFFSET = 'offset',
}

type DateFormatOptions = {
  timeZone?: string | null;
  isLocalDate?: boolean;
};

export interface Formatters {
  getStartTime: (start: Date, options?: DateFormatOptions) => string;
  getStartDate: (start: Date, options?: DateFormatOptions) => string;
  getTimezoneName: (
    timeZoneFormatType: TimezoneFormatType,
    overrideTimezone?: string,
  ) => string | undefined;
  getDuration: (start: Date, end: Date) => string;
}

export const useFormatters = (): Formatters => {
  const { selectedTimezone } = useApplicationContext();
  const { timezone } = selectedTimezone || {};

  const { t } = useTranslation();
  const { language } = useEnvironment();

  const createIntl = (
    options?: Intl.DateTimeFormatOptions,
    dateFormatOptions?: DateFormatOptions,
  ) => {
    const formatTimezone = dateFormatOptions?.isLocalDate
      ? 'UTC'
      : dateFormatOptions?.timeZone || timezone;

    return new Intl.DateTimeFormat(language, {
      timeZone: formatTimezone,
      ...options,
    });
  };

  const getStartTime = useCallback(
    (start: Date, options?: DateFormatOptions) => {
      return createIntl(
        { hour: 'numeric', minute: 'numeric' },
        options,
      ).format(start);
    },
    [timezone, language],
  );

  const getStartDate = useCallback(
    (start: Date, options?: DateFormatOptions) => {
      return createIntl(
        {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        },
        options,
      ).format(start);
    },
    [timezone, language],
  );

  const getTimezoneName = useCallback(
    (timeZoneFormatType: TimezoneFormatType, overrideTimezone?: string) => {
      const zoneId = overrideTimezone || timezone;
      if (!zoneId) {
        return;
      }

      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        timeZoneName:
          timeZoneFormatType === TimezoneFormatType.NAME ? 'long' : 'short',
        ...(zoneId ? { timeZone: zoneId } : {}),
      };

      return createIntl(options)
        .formatToParts(new Date())
        .find(({ type }) => type === 'timeZoneName')?.value;
    },
    [timezone, language],
  );

  const getDuration = useCallback((start: Date, end: Date) => {
    const duration = end.getTime() - start.getTime();
    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor(duration / (1000 * 60 * 60)) % 24;
    const minutes = Math.floor((duration / (1000 * 60)) % 60);

    const result = [];

    if (days > 0) {
      result.push(
        t('app.my-bookings-widget.bookings-details.duration.days', {
          days,
        }),
      );
    }

    if (hours > 0) {
      result.push(
        t('app.my-bookings-widget.bookings-details.duration.hours', {
          hours,
        }),
      );
    }

    if (minutes > 0) {
      result.push(
        t('app.my-bookings-widget.bookings-details.duration.minutes', {
          minutes,
        }),
      );
    }

    return result.join(' ');
  }, []);

  return {
    getStartTime,
    getStartDate,
    getTimezoneName,
    getDuration,
  };
};
